import React, {useCallback, useMemo} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import classnames from "classnames";
import {InputForm} from "../../base/input/input";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {PurchaseHistoryStatusList, PurchaseStatuses} from "../../../views/panel/common/purchases/constants";
import MultiSelectVendorsSearchSection from "../../text-fields/autocomplete/vendor-autocomplete/multi-select-vendors-search-section";
import {useTranslation} from "react-i18next";

const formNames = {
    keyword: 'keyword',
    partNo: 'partNo',
    vendors: 'vendors',
    status: 'status',
    startDate: 'startDate',
    endDate: 'endDate',
};

/**
 * Shared purchase list filter form (orders + history).
 * Mirrors OrderSearchSection: hide vendor multi-select under vendor scope; optional part field on items tabs.
 */
const PurchasesSearchSection = ({
                                    searchData,
                                    hideVendorsMultiSelect = false,
                                    showPartNumberField = false,
                                    statusVariant = 'orders',
                                }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.search'});

    const schema = useMemo(() => Yup.object().shape({
        [formNames.keyword]: Yup.string().trim().nullable(),
        [formNames.partNo]: Yup.string().trim().nullable(),
        [formNames.status]: Yup.string().trim().nullable(),
        [formNames.vendors]: Yup.array().nullable(),
        [formNames.startDate]: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .maxDate(Yup.ref(formNames.endDate), ValidateMessages.lessThan(t('endDate')))
            .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
            .nullable(),
        [formNames.endDate]: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .minDate(Yup.ref(formNames.startDate), ValidateMessages.greaterThan(t('startDate')))
            .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
            .nullable(),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.keyword]: searchData.filters?.keyword ?? '',
        [formNames.partNo]: searchData.filters?.partNo ?? '',
        [formNames.vendors]: searchData.vendors ?? [],
        [formNames.status]: searchData.filters?.status ?? '',
        [formNames.startDate]: searchData.filters?.startDate
            ? DateUtils.serverToUTC(searchData.filters?.startDate)
            : undefined,
        [formNames.endDate]: searchData.filters?.endDate
            ? DateUtils.serverToUTC(searchData.filters?.endDate)
            : undefined,
    }), [
        searchData.filters?.endDate,
        searchData.filters?.keyword,
        searchData.filters?.partNo,
        searchData.filters?.startDate,
        searchData.filters?.status,
        searchData.vendors,
    ]);

    const statusOptions = useMemo(() => {
        if (statusVariant === 'history') {
            return [
                {value: '', id: 'all', label: t('statusAll')},
                ...PurchaseHistoryStatusList.map((status) => ({
                    value: status,
                    id: status,
                    label: status,
                })),
            ];
        }
        return [
            {value: '', id: 'all', label: t('statusAll')},
            {
                value: PurchaseStatuses.open,
                id: PurchaseStatuses.open,
                label: t('statusOpen'),
            },
            {
                value: PurchaseStatuses.closed,
                id: PurchaseStatuses.closed,
                label: t('statusClosed'),
            },
            {
                value: PurchaseStatuses.pending,
                id: PurchaseStatuses.pending,
                label: t('statusPending'),
            },
        ];
    }, [statusVariant, t]);

    /**
     * Applies form values to list search filters (and vendors multi-select when system scope).
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters({
            keyword: values[formNames.keyword]?.trim() ?? '',
            partNo: values[formNames.partNo]?.trim() ?? '',
            status: values[formNames.status] || undefined,
            startDate: values[formNames.startDate]
                ? DateUtils.UTCToServer(values[formNames.startDate]).format(DateUtils.withoutOffsetFormat)
                : undefined,
            endDate: values[formNames.endDate]
                ? DateUtils.UTCToServer(values[formNames.endDate]).format(DateUtils.withoutOffsetFormat)
                : undefined,
        });
        if (!hideVendorsMultiSelect) {
            searchData.setVendors(values[formNames.vendors] ?? []);
        }
    }, [hideVendorsMultiSelect, searchData]);

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const colClassName = 'px-3 mb-3';
                return (
                    <Row className={'align-items-center justify-content-center'}>
                        {
                            !hideVendorsMultiSelect &&
                            <Col xs={12} className={colClassName}>
                                <MultiSelectVendorsSearchSection
                                    excludeDefaultVendor
                                    vendors={values[formNames.vendors] ?? []}
                                    setVendors={(vendors) => form.change(formNames.vendors, vendors)}
                                />
                            </Col>
                        }
                        <Col xs={12} className={classnames(colClassName)}>
                            <Row className={'w-100 no-gutters'}>
                                <Col className={classnames('flex-grow-1', {"pr-3": showPartNumberField})}>
                                    <InputForm
                                        onChange={(val) => form.change(formNames.keyword, val)}
                                        label={t('keyword')}
                                        name={formNames.keyword}
                                        type={'text'}
                                        required={required[formNames.keyword]}
                                        value={values[formNames.keyword] ?? ''}
                                    />
                                </Col>
                                {
                                    showPartNumberField &&
                                    <Col className={classnames('flex-grow-1')}>
                                        <InputForm
                                            onChange={(val) => form.change(formNames.partNo, val)}
                                            label={t('partNo')}
                                            name={formNames.partNo}
                                            type={'text'}
                                            required={required[formNames.partNo]}
                                            value={values[formNames.partNo] ?? ''}
                                        />
                                    </Col>
                                }
                            </Row>
                        </Col>
                        <Col xs={12} md={5} lg={4} xl={3} className={colClassName}>
                            <DropdownForm
                                label={t('status')}
                                name={formNames.status}
                                required={required[formNames.status]}
                                data={statusOptions}
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4} xl={4} className={colClassName}>
                            <DatePickerForm
                                name={formNames.startDate}
                                label={t('startDate')}
                                required={required[formNames.startDate]}
                                maxDate={values[formNames.endDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4} xl={5} className={colClassName}>
                            <DatePickerForm
                                name={formNames.endDate}
                                label={t('endDate')}
                                required={required[formNames.endDate]}
                                minDate={values[formNames.startDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} className={'px-3 mt-2 align-items-center row no-gutters justify-content-center'}>
                            <Button
                                disabled={!!submitting || searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className={'py-3 px-8'}
                            >
                                {submitting || searchData.loading ? t('searching') : t('search')}
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
};

export default PurchasesSearchSection;
